import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './CustomTooltip.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { TooltipEvent } from 'primereact/tooltip/tooltipoptions';
import { Button } from 'primereact/button';
import { anchorTooltip } from '../../../utils/tooltipViewportClamp';

interface TooltipProps {
  tooltipText: string;
  additionalIcoClass?: string;
  iconClass?: string;
  /** Rozmiar ikony (`ico--xs` domyślnie) — np. `ico--xxs` przy drobnych etykietach formularza. */
  iconSizeClass?: string;
  addMargin?: boolean;
  targetRef?: string | null;
  direction?: any;
  isMobileTooltip?: boolean;
  options?: any;
  link?: string;
  linkLabel?: string;
}

const CustomTooltip: React.FC<TooltipProps> = ({
  tooltipText,
  additionalIcoClass = '',
  iconClass = 'ico--info',
  iconSizeClass = 'ico--xs',
  addMargin = true,
  targetRef = null,
  direction = 'top',
  isMobileTooltip = false,
  options,
  link,
  linkLabel,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLinkClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (link) {
        navigate(link);
      }
    },
    [link, navigate]
  );
  // Id ikony ustalone raz na życie komponentu: nowe przy każdym renderze odpinało PrimeReactowi cel
  // widocznego dymka (np. po odświeżeniu kafelka) i dymek zostawał po zjechaniu myszą.
  const generatedId = useRef(`tooltip-${uuidv4()}`).current;
  const uniqueId = targetRef || generatedId;
  const [tooltipWidth, setTooltipWidth] = useState<number | undefined>(undefined);
  const textRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<Tooltip>(null);

  // Dymek wraca na środek nad/pod celem po każdym ustawieniu pozycji przez PrimeReact (jego „fit”
  // przy krawędzi ekranu odsuwał dymek daleko od celu) — patrz `tooltipViewportClamp`.
  const handleShow = (e: TooltipEvent) => {
    if (['top', 'bottom', 'left', 'right'].includes(direction)) {
      anchorTooltip(() => tooltipRef.current?.getElement() ?? null, e.target as Element, direction);
    }
  };

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const el = textRef.current;
      if (!el) {
        return;
      }
      const textWidth = el.offsetWidth;
      setTooltipWidth(Math.min(textWidth + 2, 200));
    });

    return () => cancelAnimationFrame(id);
  }, [tooltipText, t]);

  if (!tooltipText) {
    return null;
  }

  // With interpolation options the old i18next typings broaden t()'s return type beyond ReactNode
  // ($SpecialObject / TFunctionDetailedResult) — pin it to a plain string for JSX.
  const tooltipContent = String(t(tooltipText, options));

  // Both Tooltips use appendTo=body: PrimeReact's default inline render (appendTo: null) gets
  // clipped or painted under the container when the target sits inside a popup/menu (e.g. MoreBtn's
  // `.buttons-list`) or an overflow/stacking context (table cell). Portaling to body + the global
  // `[role='tooltip'] { z-index: 10001 }` keeps it on top.
  return (
    <>
      <span
        ref={textRef}
        style={{
          visibility: 'hidden',
          left: '-2000px',
          position: 'absolute',
          fontSize: '10px',
          fontWeight: '500',
          fontFamily: 'Inter',
        }}
      >
        {link && `${t(linkLabel || 'upgrade')} `}
        {tooltipContent}
      </span>
      {!targetRef && (
        <span
          id={uniqueId}
          className={`${iconClass} ${iconSizeClass} ${addMargin ? 'ico--info-margin' : ''} ${additionalIcoClass}`}
        />
      )}
      {link ? (
        <Tooltip
          ref={tooltipRef}
          onShow={handleShow}
          target={`#${uniqueId}`}
          className={`${direction ? `tooltip--${direction}` : ''} ${
            isMobileTooltip ? 'tooltip--mobile' : ''
          }`}
          position={direction}
          style={{ width: tooltipWidth }}
          autoHide={false}
          hideDelay={50}
          appendTo={document.body}
        >
          <span>
            <Button onClick={handleLinkClick} className="btn--link ">
              {t(linkLabel || 'upgrade')}
            </Button>{' '}
            {tooltipContent}
          </span>
        </Tooltip>
      ) : (
        <Tooltip
          ref={tooltipRef}
          onShow={handleShow}
          target={`#${uniqueId}`}
          content={tooltipContent}
          className={`${direction ? `tooltip--${direction}` : ''} ${
            isMobileTooltip ? 'tooltip--mobile' : ''
          }`}
          position={direction}
          style={{ width: tooltipWidth }}
          showDelay={5}
          appendTo={document.body}
        />
      )}
    </>
  );
};

export default CustomTooltip;
